/**
 * Calendly Connect Component
 *
 * Handles the OAuth connection flow for Calendly
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  Calendar,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Loader2,
  ExternalLink,
  Unplug,
} from 'lucide-react'
import {
  useCalendlyIntegration,
  useInitiateCalendlyOAuth,
  useSyncCalendlyEventTypes,
  useDisconnectCalendly,
} from '@/lib/integrations/calendly'

export interface CalendlyConnectProps {
  onConnected?: () => void
}

export function CalendlyConnect({ onConnected }: CalendlyConnectProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  const { data: integration, isLoading: isLoadingIntegration } = useCalendlyIntegration()
  const initiateOAuth = useInitiateCalendlyOAuth()
  const syncEventTypes = useSyncCalendlyEventTypes()
  const disconnect = useDisconnectCalendly()

  // Handle OAuth callback result
  useEffect(() => {
    const calendlyStatus = searchParams.get('calendly')

    if (calendlyStatus === 'connected') {
      onConnected?.()
      // Clear the query param
      searchParams.delete('calendly')
      setSearchParams(searchParams, { replace: true })
    } else if (calendlyStatus === 'error') {
      // Error is shown via the integration status
      searchParams.delete('calendly')
      searchParams.delete('message')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, onConnected])

  const handleConnect = async () => {
    try {
      const { authorization_url } = await initiateOAuth.mutateAsync('/integrations')
      window.location.href = authorization_url
    } catch (error) {
      console.error('Failed to initiate OAuth:', error)
    }
  }

  const handleSync = async () => {
    try {
      await syncEventTypes.mutateAsync()
    } catch (error) {
      console.error('Failed to sync:', error)
    }
  }

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync()
      setShowDisconnectConfirm(false)
    } catch (error) {
      console.error('Failed to disconnect:', error)
    }
  }

  if (isLoadingIntegration) {
    return (
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      </div>
    )
  }

  const isConnected = integration?.status === 'connected'
  const hasError = integration?.status === 'error'
  const config = integration?.config as Record<string, string> | null

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Calendly</h3>
            {isConnected && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40">
                <CheckCircle2 className="w-3 h-3" />
                Connected
              </span>
            )}
            {hasError && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/40">
                <AlertCircle className="w-3 h-3" />
                Error
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Let guests book recording times directly from your Calendly schedule
          </p>
        </div>
      </div>

      {/* Connected State */}
      {isConnected && (
        <>
          <div className="mb-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Connected as
              </span>
              {integration?.last_synced && (
                <span className="text-xs text-zinc-400">
                  Last synced: {new Date(integration.last_synced).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-900 dark:text-zinc-100">
                {config?.user_name || config?.user_email || 'Calendly User'}
              </span>
              <a
                href="https://calendly.com/event_types/user/me"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSync}
              disabled={syncEventTypes.isPending}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50"
            >
              {syncEventTypes.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync Schedules
            </button>

            {showDisconnectConfirm ? (
              <div className="flex gap-2">
                <button
                  onClick={handleDisconnect}
                  disabled={disconnect.isPending}
                  className="py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 transition-colors disabled:opacity-50"
                >
                  {disconnect.isPending ? 'Disconnecting...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  className="py-2.5 px-4 rounded-lg text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
              >
                <Unplug className="w-4 h-4" />
                Disconnect
              </button>
            )}
          </div>
        </>
      )}

      {/* Error State */}
      {hasError && (
        <>
          <div className="mb-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rose-600 dark:text-rose-400">
                {integration?.error_message || 'Connection error. Please reconnect.'}
              </p>
            </div>
          </div>

          <button
            onClick={handleConnect}
            disabled={initiateOAuth.isPending}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-[#6369D1] hover:bg-[#6369D1]/90 transition-colors disabled:opacity-50"
          >
            {initiateOAuth.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Reconnect
          </button>
        </>
      )}

      {/* Disconnected State */}
      {!isConnected && !hasError && (
        <button
          onClick={handleConnect}
          disabled={initiateOAuth.isPending}
          className="w-full py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-[#FF6F00] hover:bg-[#FF6F00]/90 transition-colors disabled:opacity-50"
        >
          {initiateOAuth.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Connecting...
            </span>
          ) : (
            'Connect Calendly'
          )}
        </button>
      )}
    </div>
  )
}
